import { Controller } from "@hotwired/stimulus"
import { adoptOrRollback, clearToasts } from "../../../lib/work/mutation_seam"
import { announce, ensureAnnouncer } from "../../../lib/work/announce"

// Work kit board surface engine (W2 Track A) — the kanban engine over the W1
// drag kernel. The controller is stacked with the kernel (and the Track C
// URL-state controller) on the one board root BoardComponent renders:
//
//   data-controller="ui--work-board ui--work-drag ui--work-board-url-state"
//
// It owns ZERO drag mechanics: the kernel performs every gesture and applies
// the optimistic DOM move. This engine only TRANSLATES the kernel's
// ui--work:drop into the board's domain event, plus the surface chrome the
// kernel knows nothing about — quick filters, lazy append, swimlane collapse,
// the roving card cursor.
//
// ── Events (anti-idea 2 — events are the only API, ZERO network) ────────────
//
//   out  ui--work:card-move        { card_id, from_status, to_status,
//                                    before_id, after_id, gesture_token,
//                                    lane_id, source }
//                                  ONE event per completed move (pointer drop
//                                  OR keyboard rank). Neighbour deltas only —
//                                  before_id/after_id are ids against the
//                                  target column's order with the moved card
//                                  removed, never index math (anti-idea 3).
//                                  card_id/before_id/after_id live in the
//                                  kernel item-id space (the card's
//                                  data-item-id — the issue key backlog rows
//                                  use), the one id space every surface event
//                                  shares.
//   in   ui--work:card-move-ack    { card_id, ok, gesture_token, status? }
//                                  The HOST dispatches this after its POST.
//                                  ok → adopt (data-work-card-status follows
//                                  `status` or the moved-to status; toasts
//                                  clear). !ok → rollback per the mutation
//                                  seam: DOM order restored via the kernel's
//                                  revert(token), totals stepped back, status
//                                  attr restored, EXACTLY ONE error toast.
//                                  Echoing gesture_token is the contract; a
//                                  tokened ack for an unknown token is STALE.
//                                  The SUPERSEDED-GESTURE rule (the issue-
//                                  transitions precedent): only the newest
//                                  in-flight gesture may revert or adopt — the
//                                  kernel's rollback snapshot is WHOLE-BOARD,
//                                  so an older gesture's late revert would
//                                  clobber every newer gesture's optimistic
//                                  swap.
//   out  ui--work:card-move-stale  { card_id, gesture_token } — a tokened ack
//                                  arrived for a gesture that is no longer the
//                                  board's newest in-flight one (a later
//                                  gesture started before this ack landed —
//                                  same card or not — or the gesture already
//                                  settled): dropped, NEVER applied (no revert,
//                                  no adopt), host should refetch (the
//                                  cell-stale grammar).
//   out  ui--work:filter-toggle    { filter_key, active }
//   out  ui--work:lane-toggle      { lane_id, collapsed }
//   out  ui--work:load-more        { column_id } — only on the host-driven
//                                  path (sentinel without data-work-page-url;
//                                  with a page url the engine fetches and
//                                  appends itself, like the backlog).
//   out  ui--work:issue-open       { issue_id, source: "keyboard" }
//
// ── The host-side contract ──────────────────────────────────────────────────
//
//   document.addEventListener("ui--work:card-move", (event) => {
//     POST /move { card_id: event.detail.card_id, ... } →
//     document.dispatchEvent(new CustomEvent("ui--work:card-move-ack", {
//       detail: { card_id, ok: true, gesture_token: event.detail.gesture_token }
//     }))
//   })
//
// The host may render one toast region inside the board root
// (`[data-work-toast-region]`); the failed-ack toast renders there through the
// shared mutation seam (exactly one toast at a time). The kit never POSTs.
// Translated error copy hydrates via the root's data-work-move-error
// attribute (BoardComponent renders it; the JS literal is the last-resort
// fallback for bare fixtures, same rule as the issue body).

const KERNEL_DROP_EVENT = "ui--work:drop"
const KERNEL_DRAG_START_EVENT = "ui--work:drag-start"
const KERNEL_DRAG_CANCEL_EVENT = "ui--work:drag-cancel"
const MOVE_EVENT = "ui--work:card-move"
const MOVE_ACK_EVENT = "ui--work:card-move-ack"
const MOVE_STALE_EVENT = "ui--work:card-move-stale"
const FILTER_TOGGLE_EVENT = "ui--work:filter-toggle"
const LANE_TOGGLE_EVENT = "ui--work:lane-toggle"
const LOAD_MORE_EVENT = "ui--work:load-more"
const ISSUE_OPEN_EVENT = "ui--work:issue-open"

// Live-region copy defaults (host-overridable via the announcements Object
// value; a missing key falls back to these, so partial overrides stay
// complete). {token} interpolation via lib/work/announce.
const DEFAULT_BOARD_ANNOUNCEMENTS = {
  cardMoved: "{card_key} moved to {column}",
  cardMovedPosition: "{card_key} moved within {column}",
  moveFailed: "Changes could not be saved",
  laneCollapsed: "{label} collapsed",
  laneExpanded: "{label} expanded",
  filterOn: "{label} filter on",
  filterOff: "{label} filter off",
  filtered: "{count} cards match the active filters",
  loaded: "{count} more cards loaded",
}

// Pill chrome the toggle swaps client-side — the exact COLOR_CLASSES pair
// PillComponent renders for the active/inactive states board_component.html.erb
// picks from filter[:active]. Keep in sync with pill_component.rb.
const PILL_ACTIVE_CLASSES = ["bg-primary/10", "text-primary", "border-primary/20"]
const PILL_INACTIVE_CLASSES = ["bg-card-secondary", "text-foreground", "border-border"]

const CONTAINER_SELECTOR = "[data-ui--work-drag-target='container']"
const CARD_SELECTOR = "[data-work-card-id]"
const ITEM_SELECTOR = "[data-ui--work-drag-target='item']"
// Turbo dispatches NOTHING on a hard frame-load network failure, so every
// Turbo-path lazy load arms this one-shot unwedge; turbo:frame-missing (a
// response arrived, no matching frame) is the other failure signal.
const FRAME_LOAD_TIMEOUT = 15000
const SKELETON_CARDS = 3

export default class BoardController extends Controller {
  static targets = ["announcer", "column", "card", "lane", "sentinel"]

  // Hydration defs (SSR JSON; an absent attribute means the empty default).
  // announcements carries the BOARD's fixed camelCase keys (values free text
  // with {token} interpolation) — BoardComponent hydrates the kit's
  // work.board.announcements.* copy and merges host overrides per key.
  static values = {
    columns: { type: Array, default: [] },
    cards: { type: Array, default: [] },
    lanes: { type: Array, default: [] },
    filters: { type: Array, default: [] },
    announcements: { type: Object, default: {} },
  }

  connect() {
    if (this._bound) return // Turbo restoration: idempotent connect
    this._bound = true

    this._announcements = { ...DEFAULT_BOARD_ANNOUNCEMENTS }
    if (this.announcementsValue && typeof this.announcementsValue === "object") {
      Object.assign(this._announcements, this.announcementsValue)
    }

    // DOM-free def maps (hydration JSON; the drag DOM never enters them, so
    // getCard snapshots stay JSON.stringify-safe). Keyed by the KERNEL item
    // id — the card's `key` (the issue key), the id space every event and
    // data-item-id carries.
    this._cardDefs = this._defMap(this.cardsValue, "key")
    this._columnDefs = this._defMap(this.columnsValue)
    this._laneDefs = this._defMap(this.lanesValue)

    this._activeFilters = new Set() // filter keys currently aria-pressed
    this._collapsedLanes = new Map() // lane id -> collapsed boolean (post-toggle)
    this._inFlight = new Set() // column ids with a lazy page loading
    this._frameTimeouts = new Map() // column id -> lazy-load unwedge timeout
    this._observedSentinels = new Map() // sentinel element -> IntersectionObserver
    this._inFlightMoves = new Map() // gesture_token -> move record (rollback path)
    this._moveSeq = 0 // mint order of in-flight moves (supersession rule)
    this._hostRequested = new WeakSet() // host-driven sentinels already fired
    this._gestureOrigin = null // { container_id, order[], all[] } pre-gesture
    this._cursorEl = null

    this._errorMessage =
      this.element.getAttribute("data-work-move-error") || DEFAULT_BOARD_ANNOUNCEMENTS.moveFailed
    this._toastRegion = this.element.querySelector("[data-work-toast-region]")

    this._onKernelDrop = (event) => this._translateKernelDrop(event)
    this._onKernelDragStart = (event) => this._captureGestureStart(event)
    this._onKernelCancel = () => {
      this._gestureOrigin = null
    }
    this._boundRootClick = (event) => this._onRootClick(event)
    this._onKeydown = (event) => this._handleKeydown(event)
    this._boundFocusIn = (event) => this._onFocusIn(event)
    this._onAccordionToggle = (event) => this._onLaneAccordionToggle(event)
    this._boundFrameLoad = (event) => this._onFrameLoad(event)
    this._boundFrameMissing = (event) => this._onFrameMissing(event)
    this._onMoveAck = (event) => this._handleMoveAck(event)

    this.element.addEventListener(KERNEL_DROP_EVENT, this._onKernelDrop)
    this.element.addEventListener(KERNEL_DRAG_START_EVENT, this._onKernelDragStart)
    this.element.addEventListener(KERNEL_DRAG_CANCEL_EVENT, this._onKernelCancel)
    this.element.addEventListener("click", this._boundRootClick)
    this.element.addEventListener("keydown", this._onKeydown)
    this.element.addEventListener("focusin", this._boundFocusIn)
    this.element.addEventListener("accordion:open", this._onAccordionToggle)
    this.element.addEventListener("accordion:close", this._onAccordionToggle)
    this.element.addEventListener("turbo:frame-load", this._boundFrameLoad)
    this.element.addEventListener("turbo:frame-missing", this._boundFrameMissing)
    // Host acks dispatch on the document (the glue island lives outside the
    // board); token routing keeps two boards on one page from cross-handling.
    document.addEventListener(MOVE_ACK_EVENT, this._onMoveAck)

    this._announcer = this.hasAnnouncerTarget
      ? this.announcerTarget
      : ensureAnnouncer(this.element)

    this._seedFilters()
    this._applyFilters(false)
    this._claimKernelItems()
    this._armSentinels()
    this._setInitialCursor()

    // Host re-renders (Turbo morphs, column re-paints) reset SSR state —
    // re-apply pill states, card visibility, collapsed lanes, cursor tabindex
    // and sentinel wiring.
    this._mutation = new MutationObserver(() => this._scheduleReapply())
    this._mutation.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    if (!this._bound) return
    this._bound = false

    this.element.removeEventListener(KERNEL_DROP_EVENT, this._onKernelDrop)
    this.element.removeEventListener(KERNEL_DRAG_START_EVENT, this._onKernelDragStart)
    this.element.removeEventListener(KERNEL_DRAG_CANCEL_EVENT, this._onKernelCancel)
    this.element.removeEventListener("click", this._boundRootClick)
    this.element.removeEventListener("keydown", this._onKeydown)
    this.element.removeEventListener("focusin", this._boundFocusIn)
    this.element.removeEventListener("accordion:open", this._onAccordionToggle)
    this.element.removeEventListener("accordion:close", this._onAccordionToggle)
    this.element.removeEventListener("turbo:frame-load", this._boundFrameLoad)
    this.element.removeEventListener("turbo:frame-missing", this._boundFrameMissing)
    document.removeEventListener(MOVE_ACK_EVENT, this._onMoveAck)

    for (const timer of this._frameTimeouts.values()) clearTimeout(timer)
    this._frameTimeouts.clear()
    for (const observer of this._observedSentinels.values()) observer.disconnect()
    this._observedSentinels.clear()
    if (this._mutation) {
      this._mutation.disconnect()
      this._mutation = null
    }
    this._inFlightMoves.clear()
    this._gestureOrigin = null
  }

  // ── Public data API ───────────────────────────────────────────────────────

  // The hydrated card def by KERNEL item id (data-item-id — the issue key),
  // with the live status projected over it. Unknown id → null.
  getCard(id) {
    const def = this._cardDefs.get(id)
    if (!def) return null
    return { ...def, status: this._statusOfItem(id) ?? def.status ?? null }
  }

  // Restore the pre-gesture arrangement for one gesture (F4 Layer 0 seam for
  // hosts that route their own revert UX through the engine): kernel DOM
  // revert + this engine's totals/status/gesture bookkeeping. Eventless — the
  // HOST called it, so the host owns the compensating write and any toast.
  revertGesture(token) {
    const move = this._inFlightMoves.get(token)
    if (move) {
      this._rollbackMove(move)
      return
    }
    this._kernel()?.revert(token)
  }

  // ── Kernel drop translation (one card-move per completed gesture) ─────────

  _captureGestureStart(event) {
    const detail = event.detail
    const itemId = detail?.item_id
    if (typeof itemId !== "string" || itemId === "") return
    const container = this._kernelContainerOfItem(itemId)
    const containerId = container?.getAttribute("data-container-id") ?? null
    this._gestureOrigin = {
      container_id: containerId,
      order: containerId ? this._orderOf(containerId) : [],
      all: this._captureAllOrder(),
    }
  }

  _translateKernelDrop(event) {
    const detail = event.detail
    if (!detail || typeof detail.gesture_token !== "string" || detail.gesture_token === "") return
    const token = detail.gesture_token
    const itemId = Array.isArray(detail.moved_ids) ? detail.moved_ids[0] : null
    const fromContainerId = detail.from_container
    const toContainerId = detail.container

    // Payload gate: neighbour deltas only — index/position fields and
    // non-string ids never enter the kit. Refuse: kernel revert, no emit.
    if (!this._validMovePayload(detail) || typeof itemId !== "string" || itemId === "") {
      this._kernel()?.revert(token)
      console.error(
        "[ui--work-board] card move rejected: neighbour deltas (string ids, before_id/after_id) are the only accepted payload; got",
        detail,
      )
      return
    }

    // No-op (reorder that changed nothing): emit nothing, record nothing.
    if (toContainerId === fromContainerId && this._isSameOrder(fromContainerId)) {
      this._gestureOrigin = null
      return
    }
    this._gestureOrigin = null

    const toStatus = this._statusOfContainer(toContainerId)
    const fromStatus = this._statusOfContainer(fromContainerId)
    const laneId = this._laneIdOfContainer(toContainerId)
    const neighbours = this._neighbourIds(detail)

    // Optimistic status follow: the wrapper's data-work-card-status rides the
    // card into the target column immediately (the ack adopts or the rollback
    // restores it).
    const previousStatus = this._setWrapperStatus(itemId, toStatus)

    const move = {
      token,
      // Mint order — the superseded-gesture rule compares these (the newest
      // in-flight gesture is the only one its ack may revert or adopt).
      seq: this._moveSeq++,
      item_id: itemId,
      from_container: fromContainerId,
      to_container: toContainerId,
      from_status: fromStatus,
      to_status: toStatus,
      previous_status: previousStatus,
    }
    this._inFlightMoves.set(token, move)

    if (toContainerId !== fromContainerId) {
      this._bumpTotal(fromContainerId, -1)
      this._bumpTotal(toContainerId, 1)
    }

    this._emit(MOVE_EVENT, {
      card_id: itemId,
      from_status: fromStatus,
      to_status: toStatus,
      before_id: neighbours.before_id,
      after_id: neighbours.after_id,
      gesture_token: token,
      lane_id: laneId,
      source: detail.source === "keyboard" ? "keyboard" : "pointer",
    })

    const columnTitle = this._columnTitle(toContainerId)
    this._announceKey(
      toContainerId === fromContainerId ? "cardMovedPosition" : "cardMoved",
      { card_key: itemId, column: columnTitle },
    )
  }

  // The contract gate: string card id, neighbour deltas, no positional fields.
  _validMovePayload(detail) {
    if (!Array.isArray(detail.moved_ids) || detail.moved_ids.length !== 1) return false
    if (!detail.moved_ids.every((id) => typeof id === "string" && id !== "")) return false
    if ("index" in detail || "position" in detail) return false
    if (!(detail.before_id === null || typeof detail.before_id === "string")) return false
    if (!(detail.after_id === null || typeof detail.after_id === "string")) return false
    return true
  }

  // ── Ack seam (host POST answer → adopt or rollback) ───────────────────────

  _handleMoveAck(event) {
    const ack = event.detail
    if (!ack || typeof ack.ok !== "boolean") return
    // A late-rendered region (host morph) is picked up before any toast.
    this._toastRegion = this.element.querySelector("[data-work-toast-region]") || this._toastRegion

    const move = this._takeMoveFor(ack)
    if (!move) {
      // Tokened ack with no matching in-flight move: stale for THIS board when
      // it even names one of our cards (another board instance ignores it —
      // its cards are not ours). Tokenless unknown acks ignore silently.
      if (ack.gesture_token && typeof ack.card_id === "string" && this._itemById(ack.card_id)) {
        this._emit(MOVE_STALE_EVENT, { card_id: ack.card_id, gesture_token: ack.gesture_token })
      }
      return
    }

    // The superseded-gesture rule (issue_transitions_controller's stale-ack
    // guard, generalized): the kernel's revert(token) restores the WHOLE-BOARD
    // pre-gesture snapshot, so a gesture a later one superseded must never act
    // on its ack — reverting it would also undo every newer gesture's
    // optimistic move (and an ok would adopt a long-dead status over the newer
    // swap). Stale: dropped unapplied, host refetches. Only the NEWEST
    // in-flight gesture's ack reverts (or adopts).
    if (this._moveSuperseded(move)) {
      this._emit(MOVE_STALE_EVENT, { card_id: move.item_id, gesture_token: move.token })
      return
    }

    const result = adoptOrRollback({
      ack,
      region: this._toastRegion,
      applyAdopt: (adopted) => this._adoptMove(move, adopted),
      applyRevert: () => this._rollbackMove(move),
      errorMessage: this._errorMessage,
    })
    if (result === "reverted") {
      this._announceKey("moveFailed")
    }
  }

  // Route an ack to its in-flight move: the echoed gesture_token is the
  // contract; a tokenless (legacy) ack falls back to the oldest in-flight
  // move of the named card. Returned moves LEAVE the map (one ack, one route).
  _takeMoveFor(ack) {
    if (ack.gesture_token && this._inFlightMoves.has(ack.gesture_token)) {
      const move = this._inFlightMoves.get(ack.gesture_token)
      this._inFlightMoves.delete(ack.gesture_token)
      return move
    }
    if (!ack.gesture_token && typeof ack.card_id === "string") {
      for (const [token, move] of this._inFlightMoves) {
        if (move.item_id === ack.card_id) {
          this._inFlightMoves.delete(token)
          return move
        }
      }
    }
    return null
  }

  _adoptMove(move, ack) {
    const status = typeof ack?.status === "string" && ack.status !== "" ? ack.status : move.to_status
    this._setWrapperStatus(move.item_id, status)
  }

  // A move is superseded when any LATER-MINTED gesture is still in flight —
  // the seq comparison, not map position (deletes must not reorder the
  // verdict). Same card or not: the rollback snapshot spans the whole board,
  // so the newest in-flight gesture is the only one whose ack may touch the
  // DOM. Once everything newer has settled (either way), the move is the
  // newest and its ack rules again.
  _moveSuperseded(move) {
    for (const other of this._inFlightMoves.values()) {
      if (other.seq > move.seq) return true
    }
    return false
  }

  _rollbackMove(move) {
    this._inFlightMoves.delete(move.token)
    this._setWrapperStatus(move.item_id, move.previous_status)
    if (move.to_container !== move.from_container) {
      this._bumpTotal(move.to_container, -1)
      this._bumpTotal(move.from_container, 1)
    }
    // The DOM half: the kernel's gesture-scoped snapshot restores the card's
    // arrangement (one-shot — the snapshot is consumed).
    this._kernel()?.revert(move.token)
  }

  // ── Kernel item claim (the wrapper/item reconciliation) ───────────────────
  // The W1 card root renders its own kernel item contract (data-ui--work-drag
  // -target="item" + data-item-id), but the card root NESTS INSIDE this
  // engine's card wrapper (data-work-card-id/status/filters). The kernel
  // re-parents its items on drop — an item wired on the card root would
  // abandon its wrapper in the origin column. So the engine CLAIMS the item
  // role for the wrapper: the card root's item target is stripped, the
  // wrapper takes it over (with the card's data-item-id), and the kernel —
  // which connects AFTER this engine (stack order) — wires only wrappers.
  // The wrapper then travels as one unit: state attrs, card and all. Runs at
  // connect, on lazy-appended fragments (before insertion) and on re-apply
  // (host re-renders restore the card's own attr).
  _claimKernelItems(scope) {
    const root = scope || this.element
    for (const wrapper of root.querySelectorAll("[data-work-card-id]")) {
      this._claimWrapper(wrapper)
    }
  }

  _claimWrapper(wrapper) {
    const card = wrapper.firstElementChild
    if (card && card.getAttribute("data-ui--work-drag-target") === "item") {
      card.removeAttribute("data-ui--work-drag-target")
      if (!wrapper.hasAttribute("data-item-id")) {
        const key = card.getAttribute("data-item-id") || wrapper.getAttribute("data-work-card-id")
        if (key) wrapper.setAttribute("data-item-id", key)
      }
    }
    // Explicit opt-out (data-draggable="false" from the def) renders NO item
    // target — the kernel never wires it, the card never drags.
    if (
      wrapper.getAttribute("data-draggable") !== "false" &&
      !wrapper.hasAttribute("data-ui--work-drag-target")
    ) {
      wrapper.setAttribute("data-ui--work-drag-target", "item")
    }
  }

  // ── Quick filters ─────────────────────────────────────────────────────────

  _seedFilters() {
    this._activeFilters.clear()
    for (const pill of this._filterPills()) {
      if (pill.getAttribute("aria-pressed") === "true") {
        this._activeFilters.add(pill.getAttribute("data-work-filter-key"))
      }
    }
  }

  _filterPills() {
    return [...this.element.querySelectorAll("[data-work-filter-key]")]
  }

  _toggleFilter(pill) {
    const key = pill.getAttribute("data-work-filter-key")
    if (!key) return
    const active = pill.getAttribute("aria-pressed") !== "true"

    pill.setAttribute("aria-pressed", active ? "true" : "false")
    this._setPillChrome(pill, active)
    if (active) this._activeFilters.add(key)
    else this._activeFilters.delete(key)

    // The state change is announced FIRST so the result count (announced
    // inside _applyFilters) lands last — the live region speaks the toggle
    // and then its effect.
    this._announceKey(active ? "filterOn" : "filterOff", { label: this._pillLabel(pill) })
    this._applyFilters(true)
    this._emit(FILTER_TOGGLE_EVENT, { filter_key: key, active })
  }

  // Pill visual follows the state: the two COLOR_CLASSES sets PillComponent
  // renders for :primary/:neutral (see PILL_*_CLASSES above). The target is
  // the pill chrome — the PillComponent span inside the default bar's button
  // — falling back to the button itself for custom chrome slots.
  _setPillChrome(pill, active) {
    const target = pill.firstElementChild || pill
    const remove = active ? PILL_INACTIVE_CLASSES : PILL_ACTIVE_CLASSES
    const add = active ? PILL_ACTIVE_CLASSES : PILL_INACTIVE_CLASSES
    target.classList.remove(...remove)
    target.classList.add(...add)
  }

  _pillLabel(pill) {
    return (pill.textContent || pill.getAttribute("data-work-filter-key") || "").trim()
  }

  // A card matches the active filter set when its data-work-card-filters
  // tokens (space-separated, projected from the def's filter_keys) intersect
  // the active keys — OR semantics across filters, Jira quick-filter style.
  // Totals are NEVER touched by filtering (server truth; the header count is
  // the column total, not the filtered view count).
  _cardMatches(cardEl) {
    if (this._activeFilters.size === 0) return true
    const keys = (cardEl.getAttribute("data-work-card-filters") || "").split(/\s+/).filter(Boolean)
    return keys.some((key) => this._activeFilters.has(key))
  }

  _applyFilters(announce) {
    let visible = 0
    for (const cardEl of this.element.querySelectorAll(CARD_SELECTOR)) {
      const show = this._cardMatches(cardEl)
      cardEl.toggleAttribute("hidden", !show)
      if (show) visible += 1
    }
    // A column whose every card is filtered out shows the injected "no
    // matches" note (the server-rendered dashed box is the EMPTY-column
    // state; this is the FILTERED-empty state — different lie, different UI).
    // A column with NO cards at all keeps the server's empty-column box as
    // the one honest state — the filtered-empty note never joins it (no
    // double empty box under an active filter).
    for (const container of this._containers()) {
      const note = container.querySelector("[data-work-filter-empty]")
      if (!note) continue
      const cards = [...container.querySelectorAll(CARD_SELECTOR)]
      const anyVisible = cards.some((el) => !el.hasAttribute("hidden"))
      note.toggleAttribute("hidden", cards.length === 0 || anyVisible)
    }
    if (announce) {
      this._announceKey("filtered", { count: String(visible) })
    }
  }

  // ── Swimlanes (collapse state lives with the HOST via lane-toggle) ────────

  _onLaneAccordionToggle(event) {
    const laneRoot = event.target.closest?.("[data-work-lane-id]")
    if (!laneRoot) return
    const laneId = laneRoot.getAttribute("data-work-lane-id")
    if (!laneId) return
    // Fires AFTER the accordion controller flipped the DOM (its data-action
    // runs on the target phase, this on the bubble phase).
    const collapsed = event.type === "accordion:close"
    this._collapsedLanes.set(laneId, collapsed)
    this._emit(LANE_TOGGLE_EVENT, { lane_id: laneId, collapsed })
    this._announceKey(collapsed ? "laneCollapsed" : "laneExpanded", {
      label: this._laneTitle(laneRoot),
    })
  }

  _laneTitle(laneRoot) {
    return (laneRoot.querySelector("h3, [data-work-lane-title]")?.textContent || "").trim()
  }

  // ── Roving card cursor (within-column; cross-column is the roving concern) ──

  _cards() {
    return [...this.element.querySelectorAll(CARD_SELECTOR)]
  }

  _itemById(id) {
    if (typeof id !== "string" || id === "") return null
    return this.element.querySelector(`${ITEM_SELECTOR}[data-item-id="${CSS.escape(id)}"]`)
  }

  _cardByItemId(itemId) {
    return this._itemById(itemId)?.closest(CARD_SELECTOR) ?? null
  }

  _setCursor(cardEl) {
    this._cursorEl = cardEl
    this._normalizeTabindex()
  }

  _normalizeTabindex() {
    for (const cardEl of this._cards()) {
      const item = cardEl.querySelector(ITEM_SELECTOR)
      const target = item || cardEl
      target.tabIndex = cardEl === this._cursorEl ? 0 : -1
    }
  }

  _setInitialCursor() {
    if (this._cursorEl && this.element.contains(this._cursorEl)) {
      this._normalizeTabindex()
      return
    }
    // The first VISIBLE card — a filter may hide the column head (the cursor
    // must never seed on an invisible card: focus silently fails there).
    const first = this._cards().find((cardEl) => !cardEl.hasAttribute("hidden"))
    if (first) this._setCursor(first)
  }

  _onFocusIn(event) {
    const cardEl = event.target.closest?.(CARD_SELECTOR)
    if (cardEl && cardEl !== this._cursorEl) this._setCursor(cardEl)
  }

  // Keyboard map: Arrow/↑↓ + j/k rove the cursor WITHIN the focused card's
  // column (clamped — no wrap); Enter/o emits ui--work:issue-open. Alt+Arrow
  // rank is the KERNEL's gesture — this handler bails on Alt so the two never
  // double-handle. Cross-column roving (ArrowLeft/Right, the Space pick/drop
  // pattern) is the board roving concern — a documented follow-up, not
  // half-built here. Form fields never trigger surface keys.
  _handleKeydown(event) {
    if (event.defaultPrevented) return
    const target = event.target
    if (!(target instanceof Element)) return
    if (this._isFormField(target)) return
    if (event.altKey || event.metaKey || event.ctrlKey) return

    const cardEl = target.closest(CARD_SELECTOR)
    const key = event.key
    const withinColumnKeys = ["ArrowUp", "ArrowDown", "j", "J", "k", "K"]
    const openKeys = ["Enter", "o", "O"]

    // Interactive controls (buttons, links) keep Enter/Space for themselves.
    if (cardEl && target.closest("button, a, [role='button']") && !withinColumnKeys.includes(key)) {
      return
    }

    if (withinColumnKeys.includes(key)) {
      if (!cardEl) return
      event.preventDefault()
      const delta = key === "ArrowDown" || key === "j" || key === "J" ? 1 : -1
      this._moveCursorWithinColumn(cardEl, delta)
      return
    }

    if (openKeys.includes(key)) {
      if (!cardEl) return
      event.preventDefault()
      const itemId =
        cardEl.getAttribute("data-item-id") || cardEl.getAttribute("data-work-card-id")
      this._setCursor(cardEl)
      if (itemId) this._emit(ISSUE_OPEN_EVENT, { issue_id: itemId, source: "keyboard" })
    }
  }

  _moveCursorWithinColumn(cardEl, delta) {
    const container = cardEl.closest(CONTAINER_SELECTOR)
    if (!container) return
    // Filtered-out ([hidden]) cards are not rove candidates: the cursor never
    // lands on an invisible card (focus would silently fail there).
    const cards = [...container.querySelectorAll(CARD_SELECTOR)].filter(
      (el) => !el.hasAttribute("hidden"),
    )
    const index = cards.indexOf(cardEl)
    if (index === -1) return
    const next = cards[Math.min(cards.length - 1, Math.max(0, index + delta))]
    if (!next || next === cardEl) return // boundary: a no-op, not a wrap
    this._setCursor(next)
    const focusTarget = next.querySelector(ITEM_SELECTOR) || next
    focusTarget.focus()
  }

  _isFormField(element) {
    const tag = element.tagName ? element.tagName.toLowerCase() : ""
    if (tag === "input" || tag === "textarea" || tag === "select") return true
    if (element.isContentEditable) return true
    return element.closest("input, textarea, select, [contenteditable='true']") !== null
  }

  // ── Root clicks: filter pills, load-more sentinels ────────────────────────
  // Delegated on the root so host re-renders never rewire handlers.

  _onRootClick(event) {
    const pill = event.target.closest("[data-work-filter-key]")
    if (pill) {
      this._toggleFilter(pill)
      return
    }
    const sentinel = event.target.closest("[data-work-load-more]")
    if (sentinel) {
      this.loadMore(sentinel.getAttribute("data-work-load-more"))
    }
  }

  // ── Lazy append per column (backlog transport, column-keyed) ──────────────
  // Public seam: the IntersectionObserver calls it, hosts may call it, tests
  // call it. Two transports: when the sentinel carries data-work-page-url the
  // engine loads the page itself (Turbo frame inside the sentinel when the
  // host rendered one, @rails/request.js otherwise); a sentinel WITHOUT a
  // page url is the host-driven path — one ui--work:load-more emission, the
  // host re-renders (Turbo owns truth).

  loadMore(columnId) {
    if (!columnId || this._inFlight.has(columnId)) return
    const sentinel = this.element.querySelector(`[data-work-load-more="${CSS.escape(columnId)}"]`)
    if (!sentinel) return
    const url = sentinel.getAttribute("data-work-page-url")
    if (!url) {
      // Host-driven path: the emission IS the feature. Fire once per arm —
      // the sentinel stays in the DOM (the host re-renders the column when
      // it answers), so repeats are dropped until a host morph replaces the
      // sentinel (the re-apply pass re-arms fresh sentinels).
      if (this._hostRequested.has(sentinel)) return
      this._hostRequested.add(sentinel)
      this._emit(LOAD_MORE_EVENT, { column_id: columnId })
      return
    }
    this._inFlight.add(columnId)
    const container = this._containerEl(columnId)
    container?.setAttribute("aria-busy", "true")
    this._insertSkeletons(container)
    const frame = sentinel.querySelector("turbo-frame")
    if (frame && "src" in frame) {
      frame.src = url
      this._armFrameTimeout(columnId)
      return
    }
    this._fetchCards(columnId, url)
  }

  async _fetchCards(columnId, url) {
    try {
      const { FetchRequest } = await import("@rails/request.js")
      const response = await new FetchRequest("get", url).perform()
      if (!response.ok) throw new Error(`board page load failed: ${response.statusCode}`)
      const html = await response.text
      const doc = new DOMParser().parseFromString(html, "text/html")
      this._finishLoad(columnId, this._extractPage(doc))
    } catch (error) {
      console.error("[ui--work-board] lazy append failed:", error)
      this._abortLoad(columnId)
    }
  }

  // turbo:frame-load: the frame swapped in the next page's fragment —
  // harvest its cards, then empty the frame so a later page cannot duplicate.
  _onFrameLoad(event) {
    const frame = event.target
    if (!(frame instanceof Element) || !frame.hasAttribute("data-work-page-frame")) return
    const columnId = frame.getAttribute("data-work-page-frame")
    if (!this._inFlight.has(columnId)) return
    const page = this._extractPage(frame)
    frame.textContent = ""
    this._finishLoad(columnId, page)
  }

  // turbo:frame-missing: Turbo's real failure signal — a response arrived but
  // held no matching frame. A hard network failure dispatches NOTHING (the
  // armed timeout covers that half); whichever fires first unwedges the
  // column exactly like the FetchRequest branch's catch. Cancelable in
  // Turbo 8 — left standing, Turbo renders its "Content missing" markup INTO
  // the frame and throws; the engine owns the failure instead.
  _onFrameMissing(event) {
    const frame = event.target
    if (!(frame instanceof Element) || !frame.hasAttribute("data-work-page-frame")) return
    const columnId = frame.getAttribute("data-work-page-frame")
    if (!this._inFlight.has(columnId)) return
    event.preventDefault()
    this._abortLoad(columnId)
  }

  _armFrameTimeout(columnId) {
    const timer = setTimeout(() => {
      this._frameTimeouts.delete(columnId)
      if (this._inFlight.has(columnId)) this._abortLoad(columnId)
    }, FRAME_LOAD_TIMEOUT)
    this._frameTimeouts.set(columnId, timer)
  }

  _clearFrameTimeout(columnId) {
    const timer = this._frameTimeouts.get(columnId)
    if (timer !== undefined) {
      clearTimeout(timer)
      this._frameTimeouts.delete(columnId)
    }
  }

  // A page fragment carries a [data-work-page] wrapper whose attributes say
  // whether more pages follow (data-work-has-more) and their URL
  // (data-work-page-url); its [data-work-card-id] children are the cards.
  _extractPage(root) {
    const page = root.querySelector("[data-work-page]")
    if (!page) return { cards: [], nextUrl: null, hasMore: false }
    return {
      cards: [...page.querySelectorAll(CARD_SELECTOR)],
      nextUrl: page.getAttribute("data-work-page-url"),
      hasMore: page.getAttribute("data-work-has-more") !== "false",
    }
  }

  _finishLoad(columnId, page) {
    this._clearFrameTimeout(columnId)
    this._inFlight.delete(columnId)
    const container = this._containerEl(columnId)
    container?.querySelectorAll("[data-work-skeleton]").forEach((el) => el.remove())
    container?.removeAttribute("aria-busy")
    if (container && page.cards.length) {
      // Appended cards respect the ACTIVE filter set (never flash in a
      // filtered-out card), and their wrappers are claimed BEFORE insertion
      // so the kernel wires them on the same tick they enter the DOM.
      for (const cardEl of page.cards) {
        cardEl.toggleAttribute("hidden", !this._cardMatches(cardEl))
        this._claimWrapper(cardEl)
      }
      container.append(...page.cards)
    }
    this._setInitialCursor()
    this._normalizeTabindex()
    const sentinel = this.element.querySelector(`[data-work-load-more="${CSS.escape(columnId)}"]`)
    if (sentinel) {
      if (page.hasMore && page.nextUrl) {
        sentinel.setAttribute("data-work-page-url", page.nextUrl)
      } else {
        this._observedSentinels.get(sentinel)?.disconnect()
        this._observedSentinels.delete(sentinel)
        sentinel.remove()
      }
    }
    // Appends leave the header totals alone: the server total is
    // authoritative and the page fragment's host re-render owns the truth
    // (moves adjust ±1; appends never recompute).
    this._announceKey("loaded", { count: String(page.cards.length) })
  }

  _abortLoad(columnId) {
    this._clearFrameTimeout(columnId)
    this._inFlight.delete(columnId)
    const container = this._containerEl(columnId)
    container?.querySelectorAll("[data-work-skeleton]").forEach((el) => el.remove())
    container?.removeAttribute("aria-busy")
    // A Turbo-frame transport keeps the URL it failed to load — and Turbo
    // skips a re-assignment of the identical src — so the retry would
    // silently do nothing. The frame forgets the URL (and Turbo's complete
    // stamp) so a retry is a genuine navigation (the backlog idiom).
    const sentinel = this.element.querySelector(`[data-work-load-more="${CSS.escape(columnId)}"]`)
    const frame = sentinel?.querySelector("turbo-frame")
    frame?.removeAttribute("src")
    frame?.removeAttribute("complete")
  }

  _insertSkeletons(container) {
    if (!container) return
    for (let i = 0; i < SKELETON_CARDS; i += 1) {
      const div = document.createElement("div")
      div.setAttribute("data-work-skeleton", "")
      div.setAttribute("aria-hidden", "true")
      div.className = "bg-muted animate-pulse rounded-md h-12"
      container.appendChild(div)
    }
  }

  _armSentinels() {
    const IO = window.IntersectionObserver
    if (typeof IO !== "function") return
    for (const sentinel of this.element.querySelectorAll("[data-work-load-more]")) {
      if (this._observedSentinels.has(sentinel)) continue
      const columnId = sentinel.getAttribute("data-work-load-more")
      const observer = new IO((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.loadMore(columnId)
        }
      }, { rootMargin: "200px" })
      observer.observe(sentinel)
      this._observedSentinels.set(sentinel, observer)
    }
  }

  // ── Column totals (server total ± moved count — NEVER recomputed) ─────────

  // The "(N)" text of every header carrying this column's total (one in plain
  // mode; in laned mode the single sticky header row). The leading integer is
  // stepped and spliced back verbatim — "(12)" → "(11)", "(12+)" → "(11+)".
  _bumpTotal(columnId, delta) {
    for (const columnRoot of this._columnRoots(columnId)) {
      const totalEl = columnRoot.querySelector("[data-work-column-total]")
      if (!totalEl) continue
      const text = totalEl.textContent ?? ""
      const current = Number.parseInt(text.replace(/[^\d-]/g, ""), 10)
      if (!Number.isFinite(current)) continue
      totalEl.textContent = text.replace(/\d+/, String(current + delta))
    }
  }

  _columnRoots(columnId) {
    if (typeof columnId !== "string" || columnId === "") return []
    return [...this.element.querySelectorAll(`[data-work-column-id="${CSS.escape(columnId)}"]`)]
  }

  _columnTitle(columnId) {
    const def = this._columnDefs.get(columnId)
    if (def && typeof def.title === "string") return def.title
    const root = this._columnRoots(columnId)[0]
    return (root?.querySelector("h3")?.textContent || columnId || "").trim()
  }

  // ── Post-render re-apply (Turbo morphs / host re-renders) ─────────────────

  _scheduleReapply() {
    if (this._reapplyQueued) return
    this._reapplyQueued = true
    window.setTimeout(() => {
      this._reapplyQueued = false
      if (!this._bound) return
      this._reapply()
    }, 0)
  }

  _reapply() {
    this._mutation?.takeRecords()
    this._toastRegion = this.element.querySelector("[data-work-toast-region]") || this._toastRegion
    this._claimKernelItems()
    this._reapplyPills()
    this._applyFilters(false)
    this._reapplyCollapsed()
    this._armSentinels()
    this._setInitialCursor()
    this._normalizeTabindex()
  }

  // Fresh SSR pills reset to their server state — the engine's active set is
  // re-applied over them (aria-pressed + pill chrome).
  _reapplyPills() {
    for (const pill of this._filterPills()) {
      const key = pill.getAttribute("data-work-filter-key")
      const active = this._activeFilters.has(key)
      pill.setAttribute("aria-pressed", active ? "true" : "false")
      this._setPillChrome(pill, active)
    }
  }

  // Collapsed-state survival: if the map says collapsed but the fresh SSR
  // trigger reads expanded, re-flip through the trigger itself so the
  // accordion controller does the collapsing (no accordion internals here).
  _reapplyCollapsed() {
    for (const laneRoot of this.element.querySelectorAll("[data-work-lane-id]")) {
      const laneId = laneRoot.getAttribute("data-work-lane-id")
      const collapsed = this._collapsedLanes.get(laneId)
      if (collapsed === undefined) continue
      const trigger = laneRoot.querySelector("[data-ui--accordion-target='trigger'], [aria-expanded]")
      if (!trigger) continue
      if ((trigger.getAttribute("aria-expanded") === "false") !== collapsed) trigger.click()
    }
  }

  // ── Plumbing ──────────────────────────────────────────────────────────────

  _defMap(defs, keyField = "id") {
    const map = new Map()
    for (const def of defs || []) {
      if (!def || typeof def !== "object") continue
      const key = typeof def[keyField] === "string" && def[keyField] !== "" ? def[keyField] : def.id
      if (typeof key === "string" && key !== "") map.set(key, def)
    }
    return map
  }

  _containers() {
    return [...this.element.querySelectorAll(CONTAINER_SELECTOR)]
  }

  _containerEl(containerId) {
    if (typeof containerId !== "string" || containerId === "") return null
    return this.element.querySelector(`${CONTAINER_SELECTOR}[data-container-id="${CSS.escape(containerId)}"]`)
  }

  _orderOf(containerId) {
    const el = this._containerEl(containerId)
    return el ? this._orderWithin(el) : []
  }

  _orderWithin(containerEl) {
    return [...containerEl.querySelectorAll(ITEM_SELECTOR)].map((el) =>
      el.getAttribute("data-item-id"),
    )
  }

  _captureAllOrder() {
    const snapshot = []
    for (const el of this._containers()) {
      snapshot.push({ id: el.getAttribute("data-container-id"), ids: this._orderWithin(el) })
    }
    return snapshot
  }

  _isSameOrder(containerId) {
    const origin = this._gestureOrigin
    if (!origin || origin.container_id !== containerId) return false
    return origin.order.join(" ") === this._orderOf(containerId).join(" ")
  }

  _kernelContainerOfItem(itemId) {
    return this._itemById(itemId)?.closest(CONTAINER_SELECTOR) ?? null
  }

  _statusOfContainer(containerId) {
    const container = this._containerEl(containerId)
    return container?.closest("[data-work-status]")?.getAttribute("data-work-status") ?? null
  }

  _laneIdOfContainer(containerId) {
    const container = this._containerEl(containerId)
    return container?.closest("[data-work-lane-id]")?.getAttribute("data-work-lane-id") ?? null
  }

  _statusOfItem(itemId) {
    return this._cardByItemId(itemId)?.getAttribute("data-work-card-status") ?? null
  }

  // Set the wrapper's status attr; returns the PREVIOUS value (the rollback
  // record). The wrapper is the resolution between the kernel item (the card
  // root, id space of issue keys) and the engine wrapper (data-work-card-id).
  _setWrapperStatus(itemId, status) {
    const wrapper = this._cardByItemId(itemId)
    if (!wrapper) return null
    const previous = wrapper.getAttribute("data-work-card-status")
    if (status) wrapper.setAttribute("data-work-card-status", status)
    return previous
  }

  // The neighbour deltas pass through VERBATIM (the translate-verbatim
  // rule): the kernel already computed them in the item-id space the payload
  // carries.
  _neighbourIds(detail) {
    return {
      before_id: detail.before_id ?? null,
      after_id: detail.after_id ?? null,
    }
  }

  _kernel() {
    if (this._kernelRef === undefined) {
      try {
        this._kernelRef = this.application.getControllerForElementAndIdentifier(this.element, "ui--work-drag") || null
      } catch {
        this._kernelRef = null
      }
    }
    return this._kernelRef
  }

  // Events carry the kit's "ui--work:*" names — NOT Stimulus's default
  // `${identifier}:action` shape — so they are dispatched as CustomEvents.
  _emit(name, detail) {
    this.element.dispatchEvent(new CustomEvent(name, { bubbles: true, cancelable: true, detail }))
  }

  _announceKey(key, tokens = {}) {
    announce(this._announcer, this._announcements[key] ?? "", tokens)
  }
}
